import { useMemo } from 'react'

import type { TripSegment } from '../types'

export type DateRangeOption = 'today' | '1day' | '7days' | '30days' | 'custom'
export type SortOption = 'pro_num' | 'status' | 'shipper' | 'consignee' | 'last_updated'

export interface FilterState {
	dateRange: DateRangeOption
	anchorDate: string
	customStartDate?: string
	customEndDate?: string
	keyword: string
	status: string
	customerId: string
	shipperId: string
	consigneeId: string
	sortBy: SortOption
	sortDir: 'asc' | 'desc'
	resultsPerPage: number
	page: number
	mapMode: 'current-page' | 'show-all' // Map display mode
}

interface Props {
	segments: TripSegment[]
	filters: FilterState
	onFiltersChange: (filters: FilterState) => void
	totalResults: number
	displayedResults: number
	showDateFilter?: boolean
	showStatusFilter?: boolean
	showServerSearchPrompt?: boolean
	onServerSearch?: () => void
}

type DateWindow = { startDate: string; endDate: string }

function ChevronLeftIcon() {
	return (
		<svg aria-hidden="true" viewBox="0 0 16 16" fill="none" style={styles.navIcon}>
			<path
				d="M9.5 3.5L5 8l4.5 4.5"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

function ChevronRightIcon() {
	return (
		<svg aria-hidden="true" viewBox="0 0 16 16" fill="none" style={styles.navIcon}>
			<path
				d="M6.5 3.5L11 8l-4.5 4.5"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

const RANGE_LABELS: Record<DateRangeOption, string> = {
	today: 'Day',
	'1day': '2 Days',
	'7days': 'Week',
	'30days': 'Month',
	custom: 'Custom',
}

const ORDER_STATUS_LABELS: Record<string, string> = {
	P: 'Picked Up',
	A: 'At shipper',
	D: 'Delivered',
	V: 'Void',
}

export function normalizeStatusFilterValue(value: string) {
	const normalizedValue = value.trim()
	const orderStatusCode = normalizedValue.toUpperCase()

	if (ORDER_STATUS_LABELS[orderStatusCode]) {
		return ORDER_STATUS_LABELS[orderStatusCode]
	}

	const matchedLabel = Object.values(ORDER_STATUS_LABELS).find(
		(label) => label.toLowerCase() === normalizedValue.toLowerCase()
	)
	if (matchedLabel) {
		return matchedLabel
	}

	return normalizedValue
}

export function getSegmentStatusLabel(
	segment: Pick<TripSegment, 'order_status' | 'shipment_status'>
) {
	const shipmentStatus = segment.shipment_status?.trim()
	if (shipmentStatus) {
		return shipmentStatus
	}

	const orderStatus = segment.order_status?.trim().toUpperCase()
	if (!orderStatus) {
		return ''
	}

	return ORDER_STATUS_LABELS[orderStatus] || orderStatus
}

export function getTodayDateValue() {
	return formatDateValue(new Date())
}

export function normalizeDateWindow(startDate: string, endDate: string): DateWindow {
	if (startDate <= endDate) {
		return { startDate, endDate }
	}

	return { startDate: endDate, endDate: startDate }
}

export function resolveDateWindow(
	filters: Pick<FilterState, 'anchorDate' | 'customEndDate' | 'customStartDate' | 'dateRange'>
): DateWindow | null {
	if (filters.dateRange === 'custom') {
		if (filters.customStartDate && filters.customEndDate) {
			return normalizeDateWindow(filters.customStartDate, filters.customEndDate)
		}

		return null
	}

	const endDate = filters.anchorDate || getTodayDateValue()
	const rangeLength = getRangeLength(filters.dateRange)
	const startDate = addDays(endDate, -(rangeLength - 1))

	return { startDate, endDate }
}

function getRangeLength(range: Exclude<DateRangeOption, 'custom'>) {
	switch (range) {
		case 'today':
			return 1
		case '1day':
			return 2
		case '7days':
			return 7
		case '30days':
			return 30
	}
}

function getStepSize(range: Exclude<DateRangeOption, 'custom'>) {
	switch (range) {
		case '7days':
			return 7
		case '30days':
			return 30
		case 'today':
		case '1day':
			return 1
	}
}

function parseDateValue(value: string) {
	const [year, month, day] = value.split('-').map(Number)
	return new Date(year, month - 1, day)
}

function formatDateValue(date: Date) {
	const year = date.getFullYear()
	const month = `${date.getMonth() + 1}`.padStart(2, '0')
	const day = `${date.getDate()}`.padStart(2, '0')
	return `${year}-${month}-${day}`
}

function addDays(value: string, amount: number) {
	const date = parseDateValue(value)
	date.setDate(date.getDate() + amount)
	return formatDateValue(date)
}

function formatDateLabel(value: string) {
	return parseDateValue(value).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

function formatDateWindowLabel(dateWindow: DateWindow | null) {
	if (!dateWindow) return 'Select a custom range'

	if (dateWindow.startDate === dateWindow.endDate) {
		return formatDateLabel(dateWindow.startDate)
	}

	return `${formatDateLabel(dateWindow.startDate)} - ${formatDateLabel(dateWindow.endDate)}`
}

function getNavigationLabels(range: DateRangeOption) {
	const unit =
		range === '30days' ? 'month' : range === '7days' ? 'week' : range === 'custom' ? '' : 'day'

	if (!unit) return null

	return {
		previous: `Last ${unit}`,
		next: `Next ${unit}`,
	}
}

export function FilterBar({
	segments,
	filters,
	onFiltersChange,
	totalResults,
	displayedResults: _displayedResults,
	showDateFilter = true,
	showStatusFilter = true,
	showServerSearchPrompt = false,
	onServerSearch,
}: Props) {
	// Extract unique values for filter dropdowns
	const filterOptions = useMemo(() => {
		const statuses = new Set<string>()
		const customers = new Map<string, string>() // Map of customer_id -> customer_name
		const shippers = new Set<string>()
		const consignees = new Set<string>()

		segments.forEach((s) => {
			const statusLabel = getSegmentStatusLabel(s)
			if (statusLabel) statuses.add(statusLabel)
			if (s.customer_id) {
				// Store both ID and name (show "Unknown Customer" if name not available)
				customers.set(s.customer_id, s.customer_name || 'Unknown Customer')
			}
			if (s.meta?.shipper) shippers.add(s.meta.shipper)
			if (s.meta?.consignee) consignees.add(s.meta.consignee)
		})

		return {
			statuses: Array.from(statuses).sort(),
			customers: Array.from(customers.entries()).sort((a, b) => a[1].localeCompare(b[1])), // Sort by name
			shippers: Array.from(shippers).sort(),
			consignees: Array.from(consignees).sort(),
		}
	}, [segments])

	const handleDateRangeChange = (range: DateRangeOption) => {
		const nextFilters: FilterState = {
			...filters,
			dateRange: range,
			page: 1,
		}

		if (range === 'custom') {
			const currentWindow = resolveDateWindow(filters)
			nextFilters.customStartDate =
				filters.customStartDate || currentWindow?.startDate || filters.anchorDate
			nextFilters.customEndDate =
				filters.customEndDate || currentWindow?.endDate || filters.anchorDate
		} else if (filters.dateRange === 'custom' && filters.customEndDate) {
			nextFilters.anchorDate = filters.customEndDate
		}

		onFiltersChange(nextFilters)
	}

	const navigateDate = (direction: -1 | 1) => {
		if (filters.dateRange === 'custom') return

		onFiltersChange({
			...filters,
			anchorDate: addDays(filters.anchorDate, direction * getStepSize(filters.dateRange)),
			page: 1,
		})
	}

	const handleCustomDateChange = (field: 'customStartDate' | 'customEndDate', value: string) => {
		if (!value) return

		const currentWindow = resolveDateWindow(filters)
		let nextStartDate =
			field === 'customStartDate'
				? value
				: filters.customStartDate || currentWindow?.startDate || value
		let nextEndDate =
			field === 'customEndDate' ? value : filters.customEndDate || currentWindow?.endDate || value

		if (field === 'customStartDate' && nextStartDate > nextEndDate) {
			nextEndDate = nextStartDate
		}

		if (field === 'customEndDate' && nextEndDate < nextStartDate) {
			nextStartDate = nextEndDate
		}

		onFiltersChange({
			...filters,
			dateRange: 'custom',
			customStartDate: nextStartDate,
			customEndDate: nextEndDate,
			page: 1,
		})
	}

	const handleJumpToToday = () => {
		const today = getTodayDateValue()

		if (filters.dateRange === 'custom') {
			onFiltersChange({
				...filters,
				customStartDate: today,
				customEndDate: today,
				page: 1,
			})
			return
		}

		onFiltersChange({
			...filters,
			anchorDate: today,
			page: 1,
		})
	}

	const activeFilters = useMemo(() => {
		const active: Array<{ key: keyof FilterState; label: string; value: string }> = []
		if (filters.keyword) active.push({ key: 'keyword', label: 'Search', value: filters.keyword })
		if (showStatusFilter && filters.status) {
			active.push({
				key: 'status',
				label: 'Status',
				value: normalizeStatusFilterValue(filters.status),
			})
		}
		if (filters.customerId) {
			// Display customer name in active filter chip if available
			const customerName = filterOptions.customers.find(([id]) => id === filters.customerId)?.[1]
			active.push({
				key: 'customerId',
				label: 'Customer',
				value: customerName || filters.customerId,
			})
		}
		if (filters.shipperId)
			active.push({ key: 'shipperId', label: 'Shipper', value: filters.shipperId })
		if (filters.consigneeId)
			active.push({ key: 'consigneeId', label: 'Consignee', value: filters.consigneeId })
		return active
	}, [filters, filterOptions, showStatusFilter])

	const clearFilter = (key: keyof FilterState) => {
		onFiltersChange({ ...filters, [key]: '', page: 1 })
	}

	const totalPages = Math.ceil(totalResults / filters.resultsPerPage)
	const activeDateWindow = resolveDateWindow(filters)
	const navigationLabels = getNavigationLabels(filters.dateRange)

	return (
		<div style={styles.container}>
			{/* Top row: View tabs and date navigation */}
			{showDateFilter && (
				<div style={styles.row}>
					<div style={styles.viewSection}>
						<span style={styles.label}>Range</span>
						<div style={styles.tabGroup}>
							{(['today', '1day', '7days', '30days', 'custom'] as DateRangeOption[]).map(
								(range) => (
									<button
										key={range}
										onClick={(e) => {
											handleDateRangeChange(range)
											e.currentTarget.blur()
										}}
										style={{
											...styles.tab,
											...(filters.dateRange === range ? styles.tabActive : {}),
										}}
									>
										{RANGE_LABELS[range]}
									</button>
								)
							)}
						</div>

						{filters.dateRange === 'custom' ? (
							<div style={styles.customDateInputs}>
								<label style={styles.dateField}>
									<span style={styles.dateFieldLabel}>From</span>
									<input
										type="date"
										value={filters.customStartDate || activeDateWindow?.startDate || ''}
										onChange={(e) => handleCustomDateChange('customStartDate', e.target.value)}
										style={styles.dateInput}
									/>
								</label>
								<label style={styles.dateField}>
									<span style={styles.dateFieldLabel}>To</span>
									<input
										type="date"
										value={filters.customEndDate || activeDateWindow?.endDate || ''}
										onChange={(e) => handleCustomDateChange('customEndDate', e.target.value)}
										style={styles.dateInput}
									/>
								</label>
								<button
									onClick={(e) => {
										handleJumpToToday()
										e.currentTarget.blur()
									}}
									style={styles.todayButton}
								>
									Today
								</button>
							</div>
						) : (
							<div style={styles.dateNav}>
								<button
									onClick={(e) => {
										navigateDate(-1)
										e.currentTarget.blur()
									}}
									style={styles.navButton}
									aria-label={
										navigationLabels
											? `Go to ${navigationLabels.previous.toLowerCase()}`
											: undefined
									}
								>
									<span style={styles.navButtonContent}>
										<ChevronLeftIcon />
										<span>{navigationLabels?.previous}</span>
									</span>
								</button>
								<span style={styles.dateDisplay}>{formatDateWindowLabel(activeDateWindow)}</span>
								<button
									onClick={(e) => {
										handleJumpToToday()
										e.currentTarget.blur()
									}}
									style={styles.todayButton}
								>
									Today
								</button>
								<button
									onClick={(e) => {
										navigateDate(1)
										e.currentTarget.blur()
									}}
									style={styles.navButton}
									aria-label={
										navigationLabels ? `Go to ${navigationLabels.next.toLowerCase()}` : undefined
									}
								>
									<span style={styles.navButtonContent}>
										<span>{navigationLabels?.next}</span>
										<ChevronRightIcon />
									</span>
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Second row: Filters */}
			<div style={styles.row}>
				<div style={styles.filtersSection}>
					<span style={styles.label}>Filters</span>
					<input
						type="text"
						placeholder="Search PRO, BOL..."
						value={filters.keyword}
						onChange={(e) => onFiltersChange({ ...filters, keyword: e.target.value, page: 1 })}
						style={styles.searchInput}
					/>

					{showStatusFilter && (
						<select
							value={filters.status}
							onChange={(e) => onFiltersChange({ ...filters, status: e.target.value, page: 1 })}
							style={styles.select}
						>
							<option value="">By status</option>
							{filterOptions.statuses.map((status) => (
								<option key={status} value={status}>
									{status}
								</option>
							))}
						</select>
					)}

					<select
						value={filters.customerId}
						onChange={(e) => onFiltersChange({ ...filters, customerId: e.target.value, page: 1 })}
						style={styles.select}
					>
						<option value="">By customer</option>
						{filterOptions.customers.map(([id, name]) => (
							<option key={id} value={id}>
								{name}
							</option>
						))}
					</select>

					<select
						value={filters.shipperId}
						onChange={(e) => onFiltersChange({ ...filters, shipperId: e.target.value, page: 1 })}
						style={styles.select}
					>
						<option value="">By shipper</option>
						{filterOptions.shippers.map((s) => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>

					<select
						value={filters.consigneeId}
						onChange={(e) => onFiltersChange({ ...filters, consigneeId: e.target.value, page: 1 })}
						style={styles.select}
					>
						<option value="">By consignee</option>
						{filterOptions.consignees.map((c) => (
							<option key={c} value={c}>
								{c}
							</option>
						))}
					</select>
				</div>

				<div style={styles.sortSection}>
					<span style={styles.label}>Sort</span>
					<select
						value={filters.sortBy}
						onChange={(e) =>
							onFiltersChange({ ...filters, sortBy: e.target.value as SortOption, page: 1 })
						}
						style={styles.select}
					>
						<option value="pro_num">By PRO</option>
						<option value="status">By Status</option>
						<option value="shipper">By Shipper</option>
						<option value="consignee">By Consignee</option>
						<option value="last_updated">By Last Update</option>
					</select>
				</div>

				<div style={styles.paginationSection}>
					<span style={styles.label}>Per Page</span>
					<select
						value={filters.resultsPerPage}
						onChange={(e) =>
							onFiltersChange({ ...filters, resultsPerPage: parseInt(e.target.value), page: 1 })
						}
						style={{ ...styles.select, width: '65px' }}
					>
						<option value={10}>10</option>
						<option value={25}>25</option>
						<option value={50}>50</option>
						<option value={100}>100</option>
					</select>

					<div style={styles.pageNav}>
						<button
							onClick={(e) => {
								onFiltersChange({ ...filters, page: Math.max(1, filters.page - 1) })
								e.currentTarget.blur()
							}}
							disabled={filters.page <= 1}
							style={styles.pageButton}
						>
							◄
						</button>
						<span style={styles.pageNumber}>
							Page {filters.page} of {totalPages || 1}
						</span>
						<button
							onClick={(e) => {
								onFiltersChange({ ...filters, page: Math.min(totalPages, filters.page + 1) })
								e.currentTarget.blur()
							}}
							disabled={filters.page >= totalPages}
							style={styles.pageButton}
						>
							►
						</button>
					</div>

					<span style={styles.resultsCount}>
						{totalResults.toLocaleString()} total {totalResults === 1 ? 'record' : 'records'}
					</span>
				</div>
			</div>

			{/* API limit warning - show when we might be hitting the API limit */}
			{totalResults >= 250 && !showServerSearchPrompt && (
				<div style={styles.limitWarning}>
					<span style={styles.limitWarningText}>
						<strong>Limit Reached:</strong> Showing first 250 results. Use filters to narrow down
						your search or contact support to increase the limit.
					</span>
				</div>
			)}

			{/* Server search prompt - when keyword search returns no results */}
			{showServerSearchPrompt && (
				<div style={styles.serverSearchPrompt}>
					<div style={styles.serverSearchContent}>
						<div style={styles.serverSearchText}>
							<strong>No results found in loaded shipments.</strong>
							<br />"{filters.keyword}" may exist in other records not yet loaded.
						</div>
					</div>
					<button
						onClick={onServerSearch}
						onMouseEnter={(e) => {
							e.currentTarget.style.backgroundColor = '#2563eb'
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.backgroundColor = '#3b82f6'
						}}
						style={styles.serverSearchButton}
					>
						Search All Records
					</button>
				</div>
			)}

			{/* Active filters chips */}
			{activeFilters.length > 0 && (
				<div style={styles.chipsRow}>
					{activeFilters.map((f) => (
						<span key={f.key} style={styles.chip}>
							<span style={styles.chipClose} onClick={() => clearFilter(f.key)}>
								✕
							</span>
							<span style={styles.chipText}>{f.value}</span>
						</span>
					))}
				</div>
			)}
		</div>
	)
}

const styles: Record<string, React.CSSProperties> = {
	container: {
		backgroundColor: '#ffffff',
		padding: '10px 16px 4px 16px',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		marginBottom: '8px',
		flexWrap: 'wrap',
	},
	viewSection: {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		flexWrap: 'wrap',
	},
	label: {
		fontSize: '12px',
		color: '#6b7280',
		fontWeight: 500,
	},
	tabGroup: {
		display: 'flex',
		gap: '4px',
		backgroundColor: '#f3f4f6',
		borderRadius: '8px',
		padding: '4px',
	},
	tab: {
		padding: '6px 16px',
		border: 'none',
		backgroundColor: '#f3f4f6',
		fontSize: '14px',
		fontWeight: 500,
		color: '#6b7280',
		cursor: 'pointer',
		borderRadius: '6px',
		transition: 'background-color 0.15s, color 0.15s',
		outline: 'none',
		WebkitTapHighlightColor: 'transparent',
	},
	tabActive: {
		backgroundColor: '#ffffff',
		color: '#111827',
		fontWeight: 600,
	},
	dateNav: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		flexWrap: 'wrap',
	},
	navButton: {
		minHeight: '32px',
		padding: '0 12px',
		border: '1px solid #d0d5dd',
		backgroundColor: '#ffffff',
		fontSize: '13px',
		fontWeight: 600,
		color: '#374151',
		cursor: 'pointer',
		borderRadius: '8px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		outline: 'none',
		WebkitTapHighlightColor: 'transparent',
		whiteSpace: 'nowrap',
	},
	navButtonContent: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '6px',
	},
	navIcon: {
		width: '14px',
		height: '14px',
		flexShrink: 0,
	},
	dateDisplay: {
		fontSize: '13px',
		fontWeight: 600,
		color: '#111827',
		minWidth: '220px',
		textAlign: 'center',
		fontVariantNumeric: 'tabular-nums',
	},
	todayButton: {
		minHeight: '32px',
		padding: '0 12px',
		border: '1px solid #d0d5dd',
		backgroundColor: '#f9fafb',
		fontSize: '13px',
		fontWeight: 600,
		color: '#374151',
		cursor: 'pointer',
		borderRadius: '8px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		outline: 'none',
		WebkitTapHighlightColor: 'transparent',
		whiteSpace: 'nowrap',
	},
	customDateInputs: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		flexWrap: 'wrap',
	},
	dateField: {
		display: 'flex',
		alignItems: 'center',
		gap: '6px',
	},
	dateFieldLabel: {
		fontSize: '12px',
		color: '#6b7280',
		fontWeight: 500,
	},
	dateInput: {
		padding: '7px 10px',
		border: '1px solid #d0d5dd',
		borderRadius: '8px',
		fontSize: '16px',
		backgroundColor: '#ffffff',
		outline: 'none',
		minWidth: '148px',
		height: '32px',
		boxSizing: 'border-box',
	},
	filtersSection: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		flexWrap: 'wrap',
	},
	searchInput: {
		padding: '7px 12px',
		border: '1px solid #d0d5dd',
		borderRadius: '8px',
		fontSize: '13px',
		width: '180px',
		maxWidth: '180px',
		outline: 'none',
		height: '32px',
		boxSizing: 'border-box',
		flexShrink: 0,
	},
	select: {
		padding: '7px 10px',
		border: '1px solid #d0d5dd',
		borderRadius: '8px',
		fontSize: '13px',
		backgroundColor: '#ffffff',
		cursor: 'pointer',
		outline: 'none',
		minWidth: '100px',
		maxWidth: '180px',
		height: '32px',
		boxSizing: 'border-box',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	sortSection: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		flexShrink: 0,
	},
	paginationSection: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		flexShrink: 0,
		flexWrap: 'wrap',
	},
	pageNav: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
	},
	pageButton: {
		width: '32px',
		height: '32px',
		border: '1px solid #d0d5dd',
		borderRadius: '8px',
		background: '#ffffff',
		cursor: 'pointer',
		fontSize: '12px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		outline: 'none',
		WebkitTapHighlightColor: 'transparent',
	},
	pageNumber: {
		minWidth: '100px',
		textAlign: 'center',
		fontSize: '13px',
		fontWeight: 500,
		padding: '0 8px',
	},
	resultsCount: {
		fontSize: '13px',
		color: '#6b7280',
		marginLeft: '8px',
	},
	chipsRow: {
		display: 'flex',
		gap: '8px',
		marginTop: '8px',
	},
	chip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '6px',
		padding: '4px 10px',
		backgroundColor: '#f3f4f6',
		borderRadius: '8px',
		fontSize: '12px',
		color: '#374151',
	},
	chipClose: {
		cursor: 'pointer',
		fontSize: '10px',
		color: '#6b7280',
		width: '14px',
		height: '14px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: '50%',
		backgroundColor: '#e5e7eb',
	},
	chipText: {
		fontWeight: 500,
	},
	limitWarning: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		padding: '10px 16px',
		backgroundColor: '#fef3c7',
		borderLeft: '3px solid #f59e0b',
		borderRadius: '4px',
		fontSize: '13px',
		color: '#92400e',
		marginBottom: '8px',
	},
	limitWarningText: {
		fontWeight: 500,
		lineHeight: '1.5',
	},
	serverSearchPrompt: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: '16px',
		padding: '12px 16px',
		backgroundColor: '#eff6ff',
		borderLeft: '3px solid #3b82f6',
		borderRadius: '4px',
		marginBottom: '8px',
		flexWrap: 'wrap',
	},
	serverSearchContent: {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		flex: '1 1 auto',
	},
	serverSearchText: {
		fontSize: '13px',
		color: '#1e40af',
		lineHeight: '1.5',
	},
	serverSearchButton: {
		padding: '8px 16px',
		backgroundColor: '#3b82f6',
		color: '#ffffff',
		border: 'none',
		borderRadius: '6px',
		fontSize: '13px',
		fontWeight: 600,
		cursor: 'pointer',
		transition: 'background-color 0.15s',
		whiteSpace: 'nowrap',
		outline: 'none',
		flexShrink: 0,
	},
}
